import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { useState } from 'react';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormRegister,
  useWatch,
} from 'react-hook-form';
import { VAULT_EXPORT_REGION_OPTIONS } from '../../../../../types';
import VaultTooltipWithLink from '../../../automation/decommissioning/decom-policy-form/form-steps/shared/vault-tooltip-with-link';
import { CreateVaultExportFormValues } from '../create-vault-export-form';
import { dataScopeOptions, timeZones } from '../vault-export-constants';
import SharedEmailAutocomplete from '../../../../shared/shared-email-autocomplete';

import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import ExpandMore from '@mui/icons-material/ExpandMore';
import InfoOutlined from '@mui/icons-material/InfoOutlined';

interface CreateGroupsVaultExportProps {
  control: Control<CreateVaultExportFormValues>;
  errors: FieldErrors<CreateVaultExportFormValues>;
  register: UseFormRegister<CreateVaultExportFormValues>;
  selectedSearchMethod: string;
  exportFormatOptions: { value: string; label: string; disabled: boolean }[];
}

export default function CreateGroupsVaultExport({
  control,
  errors,
  register,
  exportFormatOptions,
}: CreateGroupsVaultExportProps) {
  const theme = useTheme();
  const [isExportSettingsExpanded, setIsExportSettingsExpanded] =
    useState(false);
  const dataScope = useWatch({ control, name: 'dataScope' });
  const isHeldData = dataScope === 'HELD_DATA';

  return (
    <>
      <Controller
        name="dataScope"
        control={control}
        render={({ field }) => (
          <FormControl sx={{ width: '50%' }} size="small">
            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{ fontWeight: 'bold' }}
            >
              Source
            </Typography>
            <TextField
              {...field}
              select
              required
              error={!!errors.dataScope}
              helperText={errors?.dataScope?.message || ' '}
            >
              {dataScopeOptions.map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  disabled={option.disabled}
                >
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </FormControl>
        )}
      />

      <Controller
        name="searchMethod"
        control={control}
        render={({ field }) => (
          <FormControl sx={{ width: '50%' }} size="small">
            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{ fontWeight: 'bold' }}
            >
              Entity
            </Typography>
            <TextField
              {...field}
              select
              required
              error={!!errors.searchMethod}
              helperText={errors?.searchMethod?.message || ' '}
            >
              <MenuItem value="ACCOUNT">Group Email Addresses</MenuItem>
            </TextField>
          </FormControl>
        )}
      />

      <Controller
        name="searchMethodTarget"
        control={control}
        render={({ field }) => {
          const selectedEmails = field.value
            ? field.value.split(',').filter(Boolean)
            : [];
          return (
            <FormControl sx={{ width: '100%' }}>
              <SharedEmailAutocomplete
                freeSolo
                sendType="Group Email Addresses"
                sources={['groups']}
                multiAutoCompleteValue={selectedEmails}
                setMultiAutoCompleteValue={(newEmails) => {
                  field.onChange(newEmails.join(','));
                }}
                errors={!!errors.searchMethodTarget}
                helperText={errors?.searchMethodTarget?.message || ' '}
                required
                renderChipsBelow={true}
              />
            </FormControl>
          );
        }}
      />

      <Stack direction="row" gap={2} sx={{ width: '100%' }}>
        {!isHeldData && (
          <Controller
            name="timeZone"
            control={control}
            render={({ field }) => (
              <FormControl sx={{ width: '50%' }} size="small">
                <Stack direction="row" alignItems="center" gap={1}>
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    sx={{ fontWeight: 'bold' }}
                  >
                    Timezone
                  </Typography>
                  <VaultTooltipWithLink
                    preset="vaultTimezone"
                    sxIcon={{ mb: 0.5 }}
                  />
                </Stack>
                <TextField {...field} select required error={!!errors.timeZone}>
                  {timeZones.map((tz) => (
                    <MenuItem key={tz} value={tz}>
                      {tz}
                    </MenuItem>
                  ))}
                </TextField>
              </FormControl>
            )}
          />
        )}
        <Controller
          name="startTime"
          control={control}
          render={({ field: { onChange, value } }) => (
            <FormControl sx={{ width: isHeldData ? '50%' : '25%' }}>
              <Stack direction="row" alignItems="center" gap={1}>
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  sx={{ fontWeight: 'bold' }}
                >
                  Date Sent (optional)
                </Typography>
                <Tooltip
                  title="Only messages sent within this date range will be exported."
                  arrow
                >
                  <InfoOutlined
                    fontSize="small"
                    color="action"
                    sx={{ mb: 0.5 }}
                  />
                </Tooltip>
              </Stack>
              <DatePicker
                value={value ? dayjs(value) : null}
                onChange={(newValue) => {
                  if (newValue === null) {
                    onChange('');
                  } else if (newValue.isValid()) {
                    onChange(newValue.format('YYYY-MM-DD'));
                  } else {
                    onChange('invalid');
                  }
                }}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                    error: !!errors.startTime,
                    helperText: errors?.startTime?.message || ' ',
                  },
                }}
              />
            </FormControl>
          )}
        />
        <Controller
          name="endTime"
          control={control}
          render={({ field: { onChange, value } }) => (
            <FormControl sx={{ width: isHeldData ? '50%' : '25%', mt: 3.5 }}>
              <DatePicker
                value={value ? dayjs(value) : null}
                onChange={(newValue) => {
                  if (newValue === null) {
                    onChange('');
                  } else if (newValue.isValid()) {
                    onChange(newValue.format('YYYY-MM-DD'));
                  } else {
                    onChange('invalid');
                  }
                }}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                    error: !!errors.endTime,
                    helperText: errors?.endTime?.message || ' ',
                  },
                }}
              />
            </FormControl>
          )}
        />
      </Stack>

      <Box sx={{ width: '50%' }}>
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography
            variant="subtitle2"
            gutterBottom
            sx={{ fontWeight: 'bold' }}
          >
            Terms (optional)
          </Typography>
          <VaultTooltipWithLink
            preset="groupsQueryTerms"
            sxIcon={{ mb: 0.5 }}
          />
        </Stack>
        <TextField
          {...register('terms')}
          placeholder="Query Terms"
          size="small"
          variant="standard"
          helperText="Example: from:username1 subject: Hello has:attachment"
          fullWidth
          error={!!errors.terms}
        />
      </Box>

      <Accordion
        elevation={0}
        disableGutters
        expanded={isExportSettingsExpanded}
        onChange={() => setIsExportSettingsExpanded(!isExportSettingsExpanded)}
        sx={{
          '&:before': { display: 'none' },
          background: 'transparent',
          boxShadow: 'none',
          border: 'none',
          width: '100%',
        }}
      >
        <AccordionSummary
          expandIcon={
            <ExpandMore sx={{ color: theme.palette.colors?.primary?.[600] }} />
          }
          sx={{
            px: 0,
            minHeight: 'auto',
            width: 'fit-content',
            backgroundColor: 'transparent !important',
            '& .MuiAccordionSummary-content': { my: 0.5, flexGrow: 0, mr: 1 },
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              color: theme.palette.colors?.primary?.[600],
              textDecoration: 'underline',
              cursor: 'pointer',
            }}
          >
            {isExportSettingsExpanded
              ? 'Hide Export Settings'
              : 'Show Export Settings'}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          <Stack direction="row" gap={4} sx={{ width: '100%' }}>
            <Controller
              name="matterRegion"
              control={control}
              render={({ field }) => (
                <FormControl sx={{ width: '50%' }} size="small">
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    sx={{ fontWeight: 'bold' }}
                  >
                    Data Region
                  </Typography>
                  <TextField {...field} select>
                    {VAULT_EXPORT_REGION_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </FormControl>
              )}
            />
            <Controller
              name="exportFormat"
              control={control}
              render={({ field }) => (
                <FormControl sx={{ width: '50%' }} size="small">
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    sx={{ fontWeight: 'bold' }}
                  >
                    Format
                  </Typography>
                  <TextField {...field} select>
                    {exportFormatOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </FormControl>
              )}
            />
          </Stack>
        </AccordionDetails>
      </Accordion>
    </>
  );
}
